import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { Download, FileText, Video, Mic, Users, ArrowRight, BookOpen } from "lucide-react";
const resourcesImage = "/assets/website/images/Healthcare_resources_and_education_77064d97.png";

export default function Resources() {
  return (
    <div>
      <main className="py-20">
        {/* Hero Image Section */}
        <div className="relative h-80 mb-20 overflow-hidden">
          <img 
            src={resourcesImage} 
            alt="Healthcare Resources and Education"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Badge variant="secondary" className="mb-4" data-testid="badge-resources">
                Resources & Library
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-testid="text-resources-title">
                Healthcare AI Knowledge Center
              </h1>
              <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-resources-description">
                Access our comprehensive library of whitepapers, case studies, webinars, 
                and implementation guides for healthcare AI solutions.
              </p>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto max-w-7xl">

          <div className="grid lg:grid-cols-3 gap-8 mb-16 place-items-center justify-items-center">
            <Card className="hover-elevate" data-testid="card-whitepaper-1">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-6 w-6 text-primary" />
                  <Badge variant="outline">Whitepaper</Badge>
                </div>
                <CardTitle className="text-lg">
                  ROI Analysis: AI-Powered Clinical Documentation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Comprehensive analysis of time savings, cost reduction, and quality improvements 
                  from implementing ambient scribe technology across 50+ clinical sites.
                </p>
                <div className="flex gap-2 mb-4">
                  <Badge variant="outline" className="text-xs">ROI Analysis</Badge>
                  <Badge variant="outline" className="text-xs">Case Study</Badge>
                </div>
                <Button variant="outline" size="sm" className="w-full justify-between group">
                  <Download className="h-4 w-4" />
                  Download PDF
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            <Card className="hover-elevate" data-testid="card-guide-1">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <BookOpen className="h-6 w-6 text-primary" />
                  <Badge variant="outline">Implementation Guide</Badge>
                </div>
                <CardTitle className="text-lg">
                  Healthcare AI Implementation Playbook
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Step-by-step guide for healthcare leaders planning AI implementation, 
                  including change management, training protocols, and success metrics.
                </p>
                <div className="flex gap-2 mb-4">
                  <Badge variant="outline" className="text-xs">Implementation</Badge>
                  <Badge variant="outline" className="text-xs">Best Practices</Badge>
                </div>
                <Button variant="outline" size="sm" className="w-full justify-between group">
                  <Download className="h-4 w-4" />
                  Download PDF
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            <Card className="hover-elevate" data-testid="card-research-1">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-6 w-6 text-primary" />
                  <Badge variant="outline">Research Paper</Badge>
                </div>
                <CardTitle className="text-lg">
                  Clinical Burnout Reduction Through AI Automation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Peer-reviewed research on the impact of AI-powered workflow automation 
                  on clinician satisfaction and burnout rates across multiple specialties.
                </p>
                <div className="flex gap-2 mb-4">
                  <Badge variant="outline" className="text-xs">Research</Badge>
                  <Badge variant="outline" className="text-xs">Clinical Outcomes</Badge>
                </div>
                <Button variant="outline" size="sm" className="w-full justify-between group">
                  <Download className="h-4 w-4" />
                  Download PDF
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-8" data-testid="text-webinars-title">
              Webinars & Educational Content
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="hover-elevate" data-testid="card-webinar-1">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-3">
                    <Video className="h-6 w-6 text-primary" />
                    <Badge variant="outline">On-Demand Webinar</Badge>
                  </div>
                  <CardTitle className="text-lg">
                    AI in Emergency Medicine: Real-World Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    60-minute session featuring emergency physicians discussing practical 
                    AI implementation experiences and measured outcomes.
                  </p>
                  <div className="text-sm text-muted-foreground mb-4">
                    Duration: 60 minutes | CME Credits Available
                  </div>
                  <Button variant="outline" size="sm" className="justify-between group">
                    <Video className="h-4 w-4" />
                    Watch Now
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover-elevate" data-testid="card-webinar-2">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-3">
                    <Mic className="h-6 w-6 text-primary" />
                    <Badge variant="outline">Podcast Series</Badge>
                  </div>
                  <CardTitle className="text-lg">
                    Healthcare AI Leadership Conversations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Weekly podcast featuring healthcare executives and AI thought leaders 
                    discussing trends, challenges, and future opportunities.
                  </p>
                  <div className="text-sm text-muted-foreground mb-4">
                    12 Episodes | Updated Weekly
                  </div>
                  <Button variant="outline" size="sm" className="justify-between group">
                    <Mic className="h-4 w-4" />
                    Listen Now
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="hover-elevate" data-testid="card-toolkit">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <Users className="h-6 w-6 text-primary" />
                  <CardTitle>Implementation Toolkit</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Complete set of templates, checklists, and frameworks for successful 
                  AI implementation in healthcare organizations.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Project planning templates
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Training materials
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Success measurement frameworks
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Change management guides
                  </li>
                </ul>
                <Button variant="outline" size="sm" className="w-full justify-between group">
                  <Download className="h-4 w-4" />
                  Access Toolkit
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            <Card className="hover-elevate" data-testid="card-integration">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-6 w-6 text-primary" />
                  <CardTitle>Integration Documentation</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Technical documentation and API guides for integrating AIscendus solutions 
                  with existing healthcare systems and workflows.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    EHR integration guides
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    API documentation
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Security configurations
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Troubleshooting guides
                  </li>
                </ul>
                <Button variant="outline" size="sm" className="w-full justify-between group">
                  <BookOpen className="h-4 w-4" />
                  View Documentation
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4" data-testid="text-newsletter-title">
              Stay Updated with Latest Resources
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Subscribe to our resource newsletter and get notified when we publish new 
              whitepapers, case studies, and educational content.
            </p>
            <Button size="lg" data-testid="button-subscribe-resources">
              Subscribe to Resource Updates
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}